import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  Overflow,
  FlexDirection,
  FontFamily,
  Heading2,
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import type { List } from 'immutable'
import { useSelector } from 'react-redux'

import GoustoImage from 'components/Image'
import { ProductButtons } from 'components/Product/Buttons'
import { getAllergenListFromAttributes } from 'components/Product/Detail/getAllergenListFromAttributes'
import { SubIngredients } from 'routes/Menu/components/Detail/SubIngredients'
import type { Product } from 'routes/OrderConfirmation/types/products'
import { getAgeVerified } from 'selectors/user'
import { getProductLimitReached } from 'utils/basketProductLimits'
import { formatPrice } from 'utils/format'

type ProductDetailAttribute = {
  title: string
  value: string
  unit: string | null
}

type ProductDetailModalProps = {
  ageRestricted: Product['ageRestricted']
  attributes?: ProductDetailAttribute[]
  canEditOrder: boolean
  description: Product['description']
  imageMedia?: List<unknown>
  id: Product['id']
  isOpen: boolean
  listPrice: Product['listPrice']
  onAdd?: (productId: string) => void
  onRemove?: (productId: string) => void
  onVerifyAge?: (verified: boolean, showPopUp: boolean) => Promise<void>
  onVisibilityChange: () => void
  title: Product['title']
}

const mapProductState = (state: any, productId: string, ageRestricted: boolean) => {
  const ageVerified = getAgeVerified(state)
  const outOfStock = state.productsStock.get(productId, 0) <= 0
  const limitReached = getProductLimitReached(
    productId,
    state.basket,
    state.products,
    state.productsCategories,
  )

  return {
    inProgress: state.pending.get('USER_AGE_VERIFY', false),
    isAgeVerificationRequired: !ageVerified && ageRestricted,
    isAvailable: !outOfStock && !limitReached,
    limitReached,
    outOfStock,
    qty: state.basket.getIn(['products', productId], 0),
  }
}

const ProductTitle = withCitrusTheme(Heading2, (theme) => ({
  ...(theme.responsiveFont([6, null, 7, 10]) as object),
  maxWidth: '90%',
}))

const AttributesWrapper = withCitrusTheme(Box, (theme) => ({
  color: theme.color(Color.ColdGrey_800),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const ProductImageFrame = withCitrusTheme(Box, (theme) => ({
  aspectRatio: '3 / 2',
  backgroundColor: theme.color(Color.ColdGrey_100),
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  '& img': {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
}))

const FooterContent = withCitrusTheme(Box, () => ({
  overflowX: Overflow.Visible,
  overflowY: Overflow.Visible,
  position: 'relative',
  width: '100%',
}))

export const ProductDetailModal = ({
  ageRestricted,
  attributes = [],
  canEditOrder,
  description,
  imageMedia = Immutable.List(),
  id,
  isOpen,
  listPrice,
  onAdd = () => {},
  onRemove = () => {},
  onVerifyAge = async () => {},
  onVisibilityChange,
  title,
}: ProductDetailModalProps) => {
  const { inProgress, isAgeVerificationRequired, isAvailable, limitReached, outOfStock, qty } =
    useSelector((state) => mapProductState(state, id, ageRestricted))

  const immutableAttributes = React.useMemo(() => Immutable.fromJS(attributes || []), [attributes])
  const allergens = React.useMemo(
    () => getAllergenListFromAttributes(immutableAttributes),
    [immutableAttributes],
  )
  const displayAttributes = React.useMemo(
    () =>
      attributes
        .filter((attribute) => attribute.value)
        .map((attribute) => ({
          key: attribute.title,
          label: attribute.title,
          value: `${attribute.value}${attribute.unit || ''}`,
        })),
    [attributes],
  )

  return (
    <Modal
      isOpen={isOpen}
      name="order-confirmation-product-detail-modal"
      onRequestClose={onVisibilityChange}
    >
      <ModalClose onClose={onVisibilityChange} />
      <ModalHeader>
        <ProductTitle data-testing="productDetailTitle">{title}</ProductTitle>
      </ModalHeader>
      <ModalBody paddingH={0} paddingV={0}>
        <Box
          data-testing="productDetail"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={5}
          paddingBottom={4}
        >
          {imageMedia.size > 0 && (
            <ProductImageFrame data-testing="productDetailImageFrame">
              <GoustoImage contain media={imageMedia} title={title} />
            </ProductImageFrame>
          )}
          <Box paddingH={[4, 6]}>
            <Body2 color={Color.ColdGrey_800}>
              <SubIngredients allergens={allergens.toArray()} subIngredients={description} />
            </Body2>
          </Box>
          {displayAttributes.length > 0 && (
            <AttributesWrapper paddingH={[4, 6]}>
              {displayAttributes.map((attribute) => (
                <Body2 color={Color.ColdGrey_800} key={attribute.key}>
                  <strong>{attribute.label}</strong>
                  {': '}
                  {attribute.value}
                </Body2>
              ))}
            </AttributesWrapper>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <FooterContent>
          <Box paddingBottom={2}>
            <Text color={Color.ColdGrey_900} fontFamily={FontFamily.Bold} size={3}>
              {formatPrice(listPrice)}
            </Text>
          </Box>
          <ProductButtons
            canEditOrder={canEditOrder}
            countLabel={(count) => `${count} added`}
            inProgress={inProgress}
            isAgeVerificationRequired={isAgeVerificationRequired}
            isAvailable={isAvailable && canEditOrder}
            limitReached={limitReached}
            onAdd={onAdd}
            onRemove={onRemove}
            onVerifyAge={onVerifyAge}
            outOfStock={outOfStock}
            productId={id}
            qty={qty}
            showPopUp
          />
        </FooterContent>
      </ModalFooter>
    </Modal>
  )
}
